import React, { useState, useEffect } from "react";

import FormPremisesDetails from "./FormPremisesDetails";
import Summary from "./Summary";
import LandingPage from "./LandingPage";
import Layout from "./Layout";
import PersonalDetails from "./PersonDetails";
import LoadPage from "./LoadPage";
import Success from "./Success";
import PinPayment from "./PinPayment";
import Error from "./Error";

const DEFAULT_FORM = {
  type: "rta",
  premises_address_line_1: "",
  premises_address_line_2: "",
  premises_suburb: "",
  premises_state: "TAS",
  premises_postcode: "",
  name: "",
  email: "",
  address_line_1: "",
  address_line_2: "",
  suburb: "",
  state: "TAS",
  postcode: "",
  fixed_term: "",
  rent_frequency: "Week",
  lease_start_date : new Date()
};

const STEPS = {
  INTRO: 1,
  PREMISES: 2,
  BUYER: 3,
  SUMMARY: 4,
  PAYMENT: 5,
  FAQ: 6,
  ERROR: 7
};

export default function UserForm() {
  const [response, setResponse] = useState({});
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [premisesErrors, setPremisesErrors] = useState({});
  const [personErrors, setPersonErrors] = useState({});
  const [unexpectedError, setUnexpectedError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState({});
  const [criticalError, setCriticalError] = useState({});
  const [captcha, setCaptcha] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const nextStep = () => {
    setPage((page) => page + 1);
  };

  const prevStep = () => {
    setPage((page) => page - 1);
  };

  const handleChange = (input) => (e) => {
    setForm({ ...form, [input]: e.target.value.trim() });
  };

  const handleProductSelection = async (option) => {
    setIsLoading(true);
    try {
      var body = {};
      var res = await fetch(`/api/price/${option}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      body = await res.json();
      if (body.hasOwnProperty("error")) {
        setIsLoading(false);
        handleError(body);
      } else {
        setIsLoading(false);
        setProduct(body);
        nextStep();
      }
    } catch (e) {
      setUnexpectedError({
        statusCode: 500,
        error: "Unexpected Error",
        message: "Unexpected Error Occured - Please try again.",
      });
    }
  };

  const handleSubmit = async (token) => {
    setIsLoading(true);
    setUnexpectedError({});
    try {
      var body = JSON.stringify({
        ...form,
        productId: product.id,
        card_token: token,
        recaptcha_token: captcha,
      });
      var res = await fetch("/api/forms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
      });
      body = await res.json();
      if (body.hasOwnProperty("error")) {
        setSucceeded(false);
        handleError(body);
        setResponse(body);
      } else {
        setResponse(body);
        setSucceeded(true);
      }
    } catch (e) {
      setUnexpectedError({
        statusCode: 500,
        error: "Unexpected Error",
        message: "Unexpected Error Occured - Please try again.",
      });
      setPage(STEPS.PREMISES);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (response) => {
    if (response.type === "Validation") {
      const errors = response.errors.reduce((total, individual) => {
        return { ...total, [individual.path]: individual.message };
      }, {});

      if (Object.keys(errors)[0].startsWith("premises")) {
        setPremisesErrors(errors);
        setPersonErrors(errors);
        setPage(STEPS.PREMISES);
      } else {
        setPersonErrors(errors);
        setPage(STEPS.BUYER);
      }
    }

    if (response.error === "Unexpected Error Occurred") {
      setCriticalError(response);
      setPage(STEPS.ERROR);
    }
    if (response.error === "Product Error Occurred") {
      setUnexpectedError(response);
    }
    if (response.error === "Unexpected Error") {
      setUnexpectedError(response);
      setPage(STEPS.PREMISES);
    }

    if (response.error === "Payment Error") {
      setError(response.message);
      setPage(STEPS.PAYMENT);
    }
  };

  if (isLoading) {
    return <LoadPage />;
  }
  if (succeeded) {
    return (
      <Layout.Tight>
        <Success
          product={product}
          setSucceeded={setSucceeded}
          setPage={setPage}
          response={response}
        />
      </Layout.Tight>
    );
  }

  switch (page) {
    case STEPS.INTRO:
      return (
        <>
          <LandingPage nextStep={nextStep} />
        </>
      );
    case STEPS.PREMISES:
      return (
        <>
          <Layout.Tight>
            <FormPremisesDetails
              nextStep={nextStep}
              prevStep={prevStep}
              handleChange={handleChange}
              setForm={setForm}
              form={form}
              setPremisesErrors={setPremisesErrors}
              premisesErrors={premisesErrors}
              unexpectedError={unexpectedError}
              handleProductSelection={handleProductSelection}
            />
          </Layout.Tight>
        </>
      );
    case STEPS.BUYER:
      return (
        <>
          <Layout.Tight>
            <PersonalDetails
              nextStep={nextStep}
              prevStep={prevStep}
              handleChange={handleChange}
              form={form}
              personErrors={personErrors}
              setPersonErrors={setPersonErrors}
            />
          </Layout.Tight>
        </>
      );
    case STEPS.SUMMARY:
      return (
        <>
          <Layout.Tight>
            <Summary
              setPage={setPage}
              product={product}
              form={form}
              prevStep={prevStep}
              nextStep={nextStep}
            />
          </Layout.Tight>
        </>
      );
    case STEPS.PAYMENT:
      return (
        <Layout.Tight>
          <PinPayment
            product={product}
            succeeded={succeeded}
            error={error}
            setError={setError}
            form={form}
            onSubmit={handleSubmit}
            prevStep={prevStep}
            setCaptcha={setCaptcha}
          />
        </Layout.Tight>
      );
    case STEPS.ERROR:
      return (
        <Layout.Tight>
          <Error error={criticalError} form={form} />
        </Layout.Tight>
      );

    default:
      <>
        <LandingPage
          handleProductSelection={handleProductSelection}
          nextStep={nextStep}
        />
      </>;
  }
}
